/* V162 — ShareEarnCard — appears after successful vote.
   Shows unique referral link + 4 quick-share buttons + progress bar
   towards lifetime-50%-off reward (3 friends = unlocked).
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { Copy, Check, Gift, MessageCircle, Mail, Send } from "lucide-react";
import { API } from "../../config";
import EarlyBirdCounter from "./EarlyBirdCounter";

const SHARE_TEXT_SK = (agentName) =>
  `Hlasujem za ${agentName} v TopRankPulse — AI agenta ktorý mi ušetrí hodiny týždenne. Pridáš sa? Ak ano oboaja získame -25% lifetime`;

export default function ShareEarnCard({ voterEmail, agentSlug, agentName, accent = "#F59E0B" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  // Create or fetch existing referral on mount
  useEffect(() => {
    let cancelled = false;
    axios
      .post(`${API}/agent-referrals/create`, { email: voterEmail, agent_slug: agentSlug })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Nepodarilo sa vytvoriť referral link."); });
    return () => { cancelled = true; };
  }, [voterEmail, agentSlug]);

  // Poll my-stats every 8s to show real-time friend conversions
  useEffect(() => {
    if (!voterEmail) return;
    let cancelled = false;
    const load = () =>
      axios
        .get(`${API}/agent-referrals/my-stats?email=${encodeURIComponent(voterEmail)}`)
        .then((r) => { if (!cancelled) setData((d) => ({ ...(d || {}), ...r.data })); })
        .catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [voterEmail]);

  const url = data?.referral_url || (data?.code ? `${window.location.origin}/agent/${agentSlug}?ref=${data.code}` : "");
  const conversions = data?.conversions || 0;
  const threshold = data?.threshold || 3;
  const unlocked = conversions >= threshold;
  const ebPosition = data?.early_bird_position;
  const ebTotal = data?.early_bird_total || 100;
  const text = SHARE_TEXT_SK(agentName || "tohto AI agenta");
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Skopíruj link:", url);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: agentName, text, url });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  if (err) {
    return (
      <div className="border border-red-700 bg-red-950/40 p-4 text-red-200 text-sm" data-testid="share-earn-error">
        {err}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="border border-zinc-800 bg-zinc-950 p-4 text-zinc-500 text-sm" data-testid="share-earn-loading">
        Pripravujem tvoj referral link...
      </div>
    );
  }

  return (
    <div
      className="border-2 p-5 bg-gradient-to-br from-zinc-950 to-purple-950/30"
      style={{ borderColor: unlocked ? "#10B981" : accent }}
      data-testid="share-earn-card"
    >
      {/* Header with gift icon + reward title */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 flex items-center justify-center flex-shrink-0"
          style={{ background: unlocked ? "rgba(16,185,129,0.15)" : "rgba(245,158,11,0.15)" }}
        >
          <Gift className="w-5 h-5" style={{ color: unlocked ? "#10B981" : accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-base text-white">
            {unlocked ? "🎉 Odomkol/a si lifetime 50% OFF!" : "🎁 Zdieľaj a získaj lifetime 50% OFF"}
          </div>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
            {unlocked
              ? "Keď bude agent LIVE, dostaneš email s aktivačným kódom a lifetime 50% zľavou (oproti default 25%)."
              : `Pošli link 3 kamarátom. Keď ${threshold} z nich zahlasujú, automatický upgrade z 25% → 50% lifetime zľavy.`}
          </p>
          {unlocked && ebPosition && (
            <div className="mt-2 inline-block bg-emerald-950 border border-emerald-500 px-2 py-1" data-testid="early-bird-position">
              <span className="text-[10px] uppercase tracking-widest font-black text-emerald-400">
                Lifetime miesto #{ebPosition} / {ebTotal}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-black mb-1.5">
          <span className="text-zinc-500">Postup k odmene</span>
          <span style={{ color: unlocked ? "#10B981" : accent }} data-testid="share-earn-progress">
            {conversions} / {threshold} {unlocked && "✓"}
          </span>
        </div>
        <div className="h-2 bg-zinc-800 overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${Math.min(100, (conversions / threshold) * 100)}%`,
              background: unlocked ? "#10B981" : accent,
            }}
          />
        </div>
      </div>

      {/* Referral URL + copy */}
      <div className="bg-zinc-900 border border-zinc-800 flex items-center mb-3">
        <code className="flex-1 text-xs text-zinc-300 px-3 py-2 truncate font-mono" data-testid="share-earn-url">
          {url}
        </code>
        <button
          onClick={copy}
          className="px-3 py-2 text-xs font-bold border-l border-zinc-800 hover:bg-zinc-800"
          style={{ color: copied ? "#10B981" : accent }}
          data-testid="share-earn-copy"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>

      {/* Quick-share buttons */}
      <div className="grid grid-cols-4 gap-2">
        <button
          onClick={nativeShare}
          className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 p-2.5 flex flex-col items-center gap-1 transition-colors"
          data-testid="share-earn-native"
        >
          <Send className="w-4 h-4 text-zinc-300" />
          <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Zdieľaj</span>
        </button>
        <a
          href={`https://wa.me/?text=${encodedText}%20${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 p-2.5 flex flex-col items-center gap-1 transition-colors"
          data-testid="share-earn-whatsapp"
        >
          <MessageCircle className="w-4 h-4 text-emerald-400" />
          <span className="text-[9px] text-zinc-500 uppercase tracking-widest">WhatsApp</span>
        </a>
        <a
          href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 p-2.5 flex flex-col items-center gap-1 transition-colors"
          data-testid="share-earn-telegram"
        >
          <Send className="w-4 h-4 text-sky-400" />
          <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Telegram</span>
        </a>
        <a
          href={`mailto:?subject=${encodeURIComponent(agentName || "AI agent")}&body=${encodedText}%20${encodedUrl}`}
          className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 p-2.5 flex flex-col items-center gap-1 transition-colors"
          data-testid="share-earn-email"
        >
          <Mail className="w-4 h-4 text-amber-400" />
          <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Email</span>
        </a>
      </div>

      {/* Leaderboard teaser */}
      <a
        href="/agent-os/leaderboard"
        className="text-[10px] text-zinc-600 hover:text-amber-400 mt-3 inline-block uppercase tracking-widest font-black"
      >
        🏆 Pozri si top referrers →
      </a>

      {/* V162.1 — Early-bird seat counter */}
      <div className="mt-3">
        <EarlyBirdCounter accent={unlocked ? "#10B981" : accent} />
      </div>
    </div>
  );
}
